import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from 'src/firebase/firebaseConfig';

export interface AppleMusicTrack {
  id: string;
  name: string;
  artistName: string;
  albumName: string;
  durationMs: number;
  previewUrl: string | null;
  artwork: {
    url: string;
    width: number;
    height: number;
  } | null;
}

export interface AppleMusicPlaylistMeta {
  id: string;
  name: string;
  description?: string;
  artwork: any;
  trackCount: number;
}

interface PlaylistResult {
  success: boolean;
  playlist: AppleMusicPlaylistMeta;
  tracks: AppleMusicTrack[];
}

export function useAppleMusicPlaylist(playlistUrl: string) {
  const [tracks, setTracks] = useState<AppleMusicTrack[]>([]);
  const [playlistMeta, setPlaylistMeta] = useState<AppleMusicPlaylistMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedUrlRef = useRef<string | null>(null);

  const fetchPlaylist = useCallback(async () => {
    if (!playlistUrl) return;
    if (fetchedUrlRef.current === playlistUrl) return; // already fetched

    setIsLoading(true);
    setError(null);

    try {
      const getPlaylist = httpsCallable<{ playlistUrl: string }, PlaylistResult>(
        functions,
        'getAppleMusicPlaylist'
      );

      const result = await getPlaylist({ playlistUrl });
      const data = result.data;

      if (data.success) {
        setTracks(data.tracks);
        setPlaylistMeta(data.playlist);
        fetchedUrlRef.current = playlistUrl;
      } else {
        setError('Failed to load playlist');
      }
    } catch (err: any) {
      console.error('Error fetching Apple Music playlist:', err);
      setError(err?.message || 'Failed to load playlist');
    } finally {
      setIsLoading(false);
    }
  }, [playlistUrl]);

  useEffect(() => {
    fetchPlaylist();
  }, [fetchPlaylist]);

  return { tracks, playlistMeta, isLoading, error, refetch: fetchPlaylist };
}
